import React, { useEffect } from "react";
import styled, { css } from "styled-components";

import { Link } from "react-router-dom";
// import { buttonHeight } from "common/const/const";
import {
  buttonHeight,
  largeComponentHeight,
  smallComponentHeight,
} from "common/const/const";
import { GlobalKeyListener } from "common/utils/globalKeyListener";

const buttonSizes = {
  large: css`
    width: 100%;
    height: ${largeComponentHeight};
  `,
  small: css`
    width: 100%;
    height: ${smallComponentHeight};
  `,
};

const buttonColors = {
  blue: css`
    color: var(--White);
    background-color: var(--MainBlue);
    &:active {
      background-color: var(--MainBlueDark);
    }
    &:disabled {
      background-color: var(--Gray);
    }
  `,
  white: css`
    color: var(--LightNavy);
    border-color: var(--Gray);
    background-color: var(--White);
    &:active {
      background-color: var(--LightGray);
    }
  `,
  "white-secondary": css`
    color: var(--MainBlue);
    background-color: var(--White);
    border-color: var(--MainBlue);
  `,
};

const buttonVariants = {
  outline: css`
    border: var(--border-width) solid;
  `,
};

const getButtonSizeStyle = (size) => {
  if (!buttonSizes[size]) {
    throw new Error(
      `Invalid button size: "${size}". Expected one of: ${Object.keys(buttonSizes).join(", ")}`
    );
  }
  return buttonSizes[size];
};

const getButtonColorStyle = (color, secondary) => {
  const key = secondary ? `${color}-secondary` : color;
  if (!buttonColors[key]) {
    throw new Error(
      `Invalid button color: "${key}". Expected one of: ${Object.keys(buttonColors).join(", ")}`
    );
  }
  return buttonColors[key];
};

const getButtonVariantStyle = (variant) => {
  if (!variant) return "";
  if (!buttonVariants[variant]) {
    throw new Error(
      `Invalid button variant: "${variant}". Expected one of: ${Object.keys(buttonVariants).join(", ")}`
    );
  }
  return buttonVariants[variant];
};

const StyledButton = styled.button`
  border-radius: var(--border-radius-8);
  font-size: var(--fontSize-14);
  font-weight: var(--fontWeight-700);
  padding: 0.5rem 1rem;
  cursor: pointer;
  border: none;
  ${(props) => getButtonSizeStyle(props.size)}
  ${(props) => getButtonColorStyle(props.color, props.secondary)}
  ${(props) => getButtonVariantStyle(props.variant)}

  &:disabled {
    cursor: not-allowed;
  }
`;

export const BaseButton = ({
  text,
  onClick,
  linkstring,
  disabled = false,
  size = "large",
  color = "white",
  variant,
  secondary = false,
  triggerKeys = null,
}) => {
  const ButtonElement = (
    <StyledButton
      type="button"
      disabled={disabled}
      size={size}
      color={color}
      variant={variant}
      onClick={onClick}
      secondary={secondary}>
      <GlobalKeyListener
        triggerKeys={triggerKeys}
        onTrigger={onClick}
        enabled={!disabled}
      />
      {text}
    </StyledButton>
  );

  return linkstring ? (
    <Link to={linkstring}>{ButtonElement}</Link>
  ) : (
    ButtonElement
  );
};

//TODO: delete later

const StyledBlueButton = ({
  text,
  onClick,
  linkstring,
  margin,
  disabled = false,
  mode = "long",
  style,
}) => {
  return linkstring !== undefined ? (
    <Link to={linkstring}>
      <StyledBlue
        disabled={disabled}
        style={{
          margin: margin,
          width: mode === "short" ? "8rem" : "100%",
          minWidth: mode === "short" ? "8rem" : "100%",
          height: mode === "short" ? `${largeComponentHeight}` : "4rem",
          ...style,
        }}>
        {text}
      </StyledBlue>
    </Link>
  ) : (
    <StyledBlue
      disabled={disabled}
      onClick={onClick}
      style={{
        margin: margin,
        width: mode === "short" ? "8rem" : "100%",
        minWidth: mode === "short" ? "8rem" : "100%",
        height: mode === "short" ? `${largeComponentHeight}` : "4rem",
        ...style,
      }}>
      {text}
    </StyledBlue>
  );
};

const StyledBlue = styled.button`
  background-color: var(--MainBlue);
  color: var(--White);
  font-size: var(--font-size-14);
  font-weight: var(--font-weight-bold);
  border: none;
  border-radius: var(--border-radius-8);
  padding: 0.5rem 1rem;
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: var(--Gray);
      cursor: not-allowed;
    `}

  &:active {
    background-color: var(--MainBlueDark);
  }
`;

const StyledWhiteButton = ({
  text,
  onClick,
  linkstring,
  mode = "long",
  disabled = false,
  style,
}) => {
  return linkstring !== undefined ? (
    <Link to={linkstring}>
      <StyledWhite
        disabled={disabled}
        style={{
          width: mode === "short" ? "8rem" : "100%",
          minWidth: mode === "short" ? "8rem" : "100%",
          height: mode === "short" ? `${largeComponentHeight}` : "4rem",
          ...style,
        }}>
        {text}
      </StyledWhite>
    </Link>
  ) : (
    <StyledWhite
      disabled={disabled}
      onClick={onClick}
      style={{
        width: mode === "short" ? "8rem" : "100%",
        minWidth: mode === "short" ? "8rem" : "100%",
        height: mode === "short" ? `${largeComponentHeight}` : "4rem",
        ...style,
      }}>
      {text}
    </StyledWhite>
  );
};

const StyledWhiteButton2 = ({ text, onClick }) => {
  return (
    <StyledWhite
      onClick={onClick}
      color="var(--MainBlue)"
      border="var(--border-width) solid var(--MainBlue)"
      style={{
        width: "100%",
        height: "4rem",
      }}>
      {text}
    </StyledWhite>
  );
};

const StyledWhite = styled.button`
  background-color: var(--White);
  color: var(--LightNavy);
  font-size: var(--font-size-14);
  font-weight: var(--font-weight-bold);
  border: var(--border-width) solid var(--Gray);
  border-radius: var(--border-radius-8);
  padding: 0.5rem 1rem;
  cursor: pointer;
  width: 100%;
  height: ${buttonHeight};

  &:active {
    background-color: var(--LightGray);
  }
`;

const StyledTextButton = ({ text, to, margin, isBold = true }) => {
  return (
    <StyledText
      // <StyledLink
      style={{
        margin: margin,
        fontWeight: isBold
          ? "var(--font-weight-bold)"
          : "var(--font-weight-regular)",
        // fontWeight: isBold ? "var(--fontWeight-700)" : "var(--fontWeight-400)",
      }}
      to={to}>
      {text}
    </StyledText>
    // </StyledLink>
  );
};

const StyledText = styled(Link)`
  border: none;
  background-color: transparent;
  color: var(--LightNavy);
  text-decoration: none;
  font-size: var(--font-size-14);
`;

// const StyledLink = styled(Link)`
//   border: none;
//   background-color: transparent;
//   color: var(--LightNavy);
//   text-decoration: none;
//   font-size: var(--font-size-14);
// `;

export {
  StyledBlueButton,
  StyledWhiteButton2,
  StyledWhiteButton,
  StyledTextButton,
};
